import React, { useState } from 'react';

export const OptimaXmlViewer = () => {
  const [docData, setDocData] = useState(null);
  const [error, setError] = useState('');

  // Pomocnicza funkcja do bezpiecznego pobierania tekstu z tagu XML
  const getNodeText = (parent, tagName, defaultValue = '') => {
    const el = parent?.querySelector(tagName);
    return el ? el.textContent.trim() : defaultValue;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const xmlText = event.target.result;
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

        // Sprawdzenie błędów parsowania XML
        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
          throw new Error('Nieprawidłowy format pliku XML');
        }

        // Parsowanie nagłówka
        const naglowek = xmlDoc.querySelector('NAGLOWEK');
        if (!naglowek) {
          throw new Error('Brak sekcji NAGLÓWEK w pliku');
        }

        const parseAdres = (parent) => {
          const adresEl = parent?.querySelector('ADRES');
          if (!adresEl) return '';
          const ulica = getNodeText(adresEl, 'ULICA');
          const kod = getNodeText(adresEl, 'KOD_POCZTOWY');
          const miasto = getNodeText(adresEl, 'MIASTO');
          return `${ulica}, ${kod} ${miasto}`.trim().replace(/^,\s*|,\s*$/g, '');
        };

        const parsedHeader = {
          numer: getNodeText(naglowek, 'NUMER_PELNY'),
          dataDokumentu: getNodeText(naglowek, 'DATA_DOKUMENTU'),
          dataWystawienia: getNodeText(naglowek, 'DATA_WYSTAWIENIA'),
          opis: getNodeText(naglowek, 'OPIS'),
          sprzedawca: {
            nazwa: getNodeText(naglowek.querySelector('SPRZEDAWCA'), 'NAZWA'),
            nip: getNodeText(naglowek.querySelector('SPRZEDAWCA'), 'NIP'),
            adres: parseAdres(naglowek.querySelector('SPRZEDAWCA')),
          },
          odbiorca: {
            nazwa: getNodeText(naglowek.querySelector('ODBIORCA'), 'NAZWA'),
            nip: getNodeText(naglowek.querySelector('ODBIORCA'), 'NIP'),
            adres: parseAdres(naglowek.querySelector('ODBIORCA')),
          },
          kwoty: {
            netto: getNodeText(naglowek.querySelector('KWOTY'), 'RAZEM_NETTO'),
            vat: getNodeText(naglowek.querySelector('KWOTY'), 'RAZEM_VAT'),
            brutto: getNodeText(naglowek.querySelector('KWOTY'), 'RAZEM_BRUTTO'),
            waluta: getNodeText(naglowek.querySelector('WALUTA'), 'SYMBOL', 'PLN'),
          },
        };

        // Parsowanie pozycji
        const pozycjeEls = xmlDoc.querySelectorAll('POZYCJE > POZYCJA');
        const parsedPozycje = Array.from(pozycjeEls).map((poz) => ({
          lp: getNodeText(poz, 'LP'),
          nazwa: getNodeText(poz.querySelector('TOWAR'), 'NAZWA'),
          kod: getNodeText(poz.querySelector('TOWAR'), 'KOD'),
          ilosc: parseFloat(getNodeText(poz, 'ILOSC') || '0').toFixed(2),
          jm: getNodeText(poz, 'JM'),
          cenaNetto: getNodeText(poz.querySelector('CENY'), 'PO_RABACIE_PLN'),
          wartoscNetto: getNodeText(poz, 'WARTOSC_NETTO'),
          wartoscBrutto: getNodeText(poz, 'WARTOSC_BRUTTO'),
          stawkaVat: getNodeText(poz.querySelector('STAWKA_VAT'), 'STAWKA'),
        }));

        setDocData({ naglowek: parsedHeader, pozycje: parsedPozycje });
        setError('');
      } catch (err) {
        setError(err.message || 'Błąd podczas przetwarzania XML');
        setDocData(null);
      }
    };

    reader.readAsText(file);
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '900px', margin: '0 auto' }}>
      <h2>WZ Optima XML</h2>

      {/* Wczytywanie pliku */}
      <div style={{ marginBottom: '20px' }}>
        <input type="file" accept=".xml" onChange={handleFileUpload} />
      </div>

      {error && <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>}

      {/* Podgląd Dokumentu */}
      {docData && (
        <div style={{ border: '1px solid #ccc', padding: '20px', borderRadius: '8px', backgroundColor: '#fff' }}>
          <header style={{ borderBottom: '2px solid #333', paddingBottom: '10px', marginBottom: '20px' }}>
            <h1 style={{ margin: 0, fontSize: '24px' }}>Dokument: {docData.naglowek.numer}</h1>
            <p style={{ color: '#666', margin: '5px 0' }}>
              Data wystawienia: <strong>{docData.naglowek.dataWystawienia}</strong> | Data operacji:{' '}
              <strong>{docData.naglowek.dataDokumentu}</strong>
            </p>
          </header>

          {/* Sekcja Stron (Sprzedawca / Odbiorca) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '4px' }}>
              <h3 style={{ marginTop: 0 }}>Sprzedawca</h3>
              <strong>{docData.naglowek.sprzedawca.nazwa}</strong>
              <br />
              NIP: {docData.naglowek.sprzedawca.nip}
              <br />
              {docData.naglowek.sprzedawca.adres}
            </div>
            <div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '4px' }}>
              <h3 style={{ marginTop: 0 }}>Odbiorca</h3>
              <strong>{docData.naglowek.odbiorca.nazwa}</strong>
              <br />
              {docData.naglowek.odbiorca.nip && <>NIP: {docData.naglowek.odbiorca.nip}<br /></>}
              {docData.naglowek.odbiorca.adres}
            </div>
          </div>

          {/* Opis */}
          {docData.naglowek.opis && (
            <div style={{ marginBottom: '20px', fontStyle: 'italic', background: '#fffbe6', padding: '10px' }}>
              <strong>Opis:</strong> {docData.naglowek.opis}
            </div>
          )}

          {/* Pozycje dokumentu */}
          <h3>Pozycje na dokumencie</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
            <thead>
              <tr style={{ background: '#eee', textAlign: 'left' }}>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Lp.</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Nazwa towaru / Kod</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>Ilość</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>Cena netto</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>VAT</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>Wartość brutto</th>
              </tr>
            </thead>
            <tbody>
              {docData.pozycje.map((poz) => (
                <tr key={poz.lp}>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>{poz.lp}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    <strong>{poz.nazwa}</strong>
                    <br />
                    <small style={{ color: '#666' }}>Kod: {poz.kod}</small>
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {poz.ilosc} {poz.jm}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {poz.cenaNetto} {docData.naglowek.kwoty.waluta}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {poz.stawkaVat}%
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {poz.wartoscBrutto} {docData.naglowek.kwoty.waluta}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Podsumowanie kwot */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: '300px', background: '#f5f5f5', padding: '15px', borderRadius: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span>Razem Netto:</span>
                <strong>{docData.naglowek.kwoty.netto} {docData.naglowek.kwoty.waluta}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span>Razem VAT:</span>
                <strong>{docData.naglowek.kwoty.vat} {docData.naglowek.kwoty.waluta}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', borderTop: '1px solid #ccc', paddingTop: '5px' }}>
                <span>Razem Brutto:</span>
                <strong>{docData.naglowek.kwoty.brutto} {docData.naglowek.kwoty.waluta}</strong>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};